import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyleSheet, css } from 'aphrodite';
import {
	Box,
	IconButton,
	Typography,
	AppBar,
	Toolbar,
	Badge,
	useMediaQuery,
	Drawer,
	List,
	ListItem,
	ListItemText,
	Button,
	Tooltip,
	Menu,
	MenuItem,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
	Menu as MenuIcon,
	PersonOutline,
	ChatBubbleOutline,
} from '@mui/icons-material';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { useNavigate } from 'react-router-dom';
import { fetchPatientData } from '../../redux/middleware/user/patient/patient_data';
import { APP_URLS } from '../../../urls.js';
import ThemeToggler from './ThemeToggler';
import { shades } from '../../../theme';
import TopBanner from '../Home/TopBanner.jsx';
import { usePatientAuth } from '../../components/patient/hooks/usePatientAuth.js';

const Navbar = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const cart = useSelector((state) => state.cart.cart);
	const cartItems = cart.items || [];
	const totalItems = cart.reduce((sum, item) => sum + (item.count || 1), 0);

	// Get authentication status from Redux state
	const { isAuthenticated } = usePatientAuth();

	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [loginMenuAnchor, setLoginMenuAnchor] = useState(null);
	const isLoginMenuOpen = Boolean(loginMenuAnchor);

	const handleLoginClick = (event) => {
		setLoginMenuAnchor(event.currentTarget);
	};

	const handleLoginClose = () => {
		setLoginMenuAnchor(null);
	};

	// Get current theme mode
	const isDarkMode = theme.palette.mode === 'dark';

	useEffect(() => {
		if (isAuthenticated) {
			dispatch(fetchPatientData());
		}
	}, [dispatch, isAuthenticated]);

	return (
		<>
			<TopBanner />

			<AppBar
				id="app-header"
				sx={{
					position: 'relative',
					backgroundColor: isDarkMode
						? 'rgba(18, 18, 18, 0.95)'
						: 'rgba(255, 255, 255, 0.95)',
					boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.3)',
					height: { xs: '3hv', sm: '6hv', md: '10vh' },
					color: isDarkMode ? shades.white[100] : shades.secondary[500],
				}}
			>
				<Toolbar
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
					}}
				>
					{/* LEFT: Logo */}
					<Box sx={{ display: 'flex', alignItems: 'center' }}>
						{isMobile && (
							<IconButton
								sx={{
									color: isDarkMode ? 'white' : 'black',
									'&:hover': {
										color: shades.primary[500],
									},
								}}
								onClick={() => setDrawerOpen(true)}
							>
								<MenuIcon />
							</IconButton>
						)}
						<Typography
							variant="h4"
							fontWeight="bold"
							sx={{
								cursor: 'pointer',
								color: isDarkMode ? 'white' : 'black',
								fontSize: { xs: '1rem', sm: '1.5rem' },
								'&:hover': {
									color: shades.primary[500],
									transition: 'color 0.3s ease',
								},
							}}
							onClick={() => navigate('/')}
						>
							M-TREAT
						</Typography>
					</Box>

					{/* CENTER: Navigation Links */}
					{!isMobile && (
						<Box sx={{ display: 'flex', gap: '2rem' }}>
							{[
								{ label: 'Home', path: '/' },
								{ label: 'Contact', path: '/app/contact' },
								{ label: 'About', path: '/app/about' },
								{ label: 'Emergency', path: '/emergency' },
							].map((link) => (
								<Typography
									key={link.label}
									variant="h9"
									sx={{
										color: isDarkMode ? 'white' : 'black',
										cursor: 'pointer',
										position: 'relative',
										'&:hover': {
											color: shades.primary[500],
											transition: 'color 0.3s ease',
											'&::after': {
												width: '100%',
											},
										},
										'&::after': {
											content: '""',
											position: 'absolute',
											bottom: '-5px',
											left: 0,
											width: '0%',
											height: '2px',
											backgroundColor: shades.primary[500],
											transition: 'width 0.3s ease',
										},
									}}
									onClick={() => navigate(link.path)}
								>
									{link.label}
								</Typography>
							))}
						</Box>
					)}

					{/* RIGHT: Cart, Chat, Profile/Login, Theme Toggler */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: { xs: '0.25rem', sm: '0.5rem', md: '1rem' },
							// Add responsive scaling for icon sizes
							'& .MuiIconButton-root': {
								width: { xs: '32px', sm: '40px', md: '48px' },
								height: { xs: '32px', sm: '40px', md: '48px' },
								'& .MuiSvgIcon-root': {
									fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.75rem' },
								},
							},
						}}
					>
						{/* Cart - Always visible */}
						<Badge badgeContent={totalItems} color="secondary">
							<Tooltip title="Cart">
								<IconButton
									sx={{
										color: isDarkMode ? 'white' : 'black',
										'&:hover': {
											color: shades.primary[500],
											transform: 'scale(1.1)',
											transition: 'all 0.3s ease',
										},
									}}
									onClick={() => navigate(APP_URLS.shoppingCart)}
								>
									<ShoppingCartOutlinedIcon />
								</IconButton>
							</Tooltip>
						</Badge>

						{/* Authentication dependent elements */}
						{isAuthenticated ? (
							<>
								{/* Chat icon - Only when authenticated */}
								<Tooltip title="Chat bot">
									<IconButton
										sx={{
											color: isDarkMode ? 'white' : 'black',
											'&:hover': {
												color: shades.primary[500],
												transform: 'scale(1.1)',
												transition: 'all 0.3s ease',
											},
										}}
									>
										<ChatBubbleOutline />
									</IconButton>
								</Tooltip>

								{/* Profile icon - Only when authenticated */}
								<Tooltip title="Profile">
									<IconButton
										sx={{
											color: isDarkMode ? 'white' : 'black',
											'&:hover': {
												color: shades.primary[500],
												transform: 'scale(1.1)',
												transition: 'all 0.3s ease',
											},
										}}
										onClick={() => navigate(APP_URLS.patientDashboard)}
									>
										<PersonOutline />
									</IconButton>
								</Tooltip>
							</>
						) : (
							/* Login button - Only when NOT authenticated */
							<>
								<Button
									variant="contained"
									onClick={handleLoginClick}
									sx={{
										color: shades.white[100],
										backgroundColor: shades.primary[400],
										fontSize: {
											xs: '0.8rem',
											sm: '0.8rem',
											md: '0.8rem',
											lg: '0.9rem',
										},
										padding: '4px 16px',
										borderRadius: '16px',
										textTransform: 'none',
										fontWeight: 500,
										transition: 'opacity 0.2s ease',
										'&:hover': {
											backgroundColor: isDarkMode
												? `${shades.primary[400]}CC`
												: `${shades.primary[600]}CC`,
											transform: 'none',
										},
										'&.MuiButton-root': {
											boxShadow: 'none',
											minWidth: 'auto',
										},
									}}
								>
									Login
								</Button>
								<Menu
									anchorEl={loginMenuAnchor}
									open={isLoginMenuOpen}
									onClose={handleLoginClose}
								>
									<MenuItem
										onClick={() => {
											navigate(APP_URLS.doctorLogin);
											handleLoginClose();
										}}
									>
										Doctor
									</MenuItem>
									<MenuItem
										onClick={() => {
											navigate(APP_URLS.pharmacyLogin);
											handleLoginClose();
										}}
									>
										Pharmacy
									</MenuItem>
									<MenuItem
										onClick={() => {
											navigate(APP_URLS.patientLogin);
											handleLoginClose();
										}}
									>
										User
									</MenuItem>
									<MenuItem
										onClick={() => {
											navigate(APP_URLS.hospitalLogin);
											handleLoginClose();
										}}
									>
										Hospital
									</MenuItem>
								</Menu>
							</>
						)}
						{/* Coming back to this when dark mode is complete */}
						{/* <ThemeToggler /> */}
					</Box>
				</Toolbar>

				{/* Mobile drawer */}
				<Drawer
					anchor="left"
					open={drawerOpen}
					onClose={() => setDrawerOpen(false)}
				>
					<List sx={{ width: 250 }}>
						{[
							{ label: 'Home', path: '/' },
							{ label: 'Contact', path: '/app/contact' },
							{ label: 'About', path: '/app/about' },
							{ label: 'Emergency', path: '/emergency' },
						].map((link) => (
							<ListItem
								button
								key={link.label}
								onClick={() => {
									navigate(link.path);
									setDrawerOpen(false);
								}}
								sx={{
									'&:hover': {
										backgroundColor: 'rgba(255, 0, 0, 0.05)',
										color: shades.primary[500],
									},
								}}
							>
								<ListItemText primary={link.label} />
							</ListItem>
						))}
					</List>
				</Drawer>
			</AppBar>
		</>
	);
};

export default Navbar;
